import { add } from 'lodash';
import { defineStore, storeToRefs } from 'pinia';

import { Notification, PagedNotifications } from '@/types/Notification';
import { dateTimeToFormattedString } from '@/utils/date';

import {
  getNotifications,
  getOneNotification,
  markNotificationsAsRead,
} from '@/flagler-api/notifications';

import { usePusherStore } from './PusherStore';
import { useUserStore } from './UserStore';
import { useRtmStore } from './RtmStore';

import { toastInfo } from '@/utils/toast';
import { onTag } from 'xss';

export const useNotificationStore = defineStore('NotificationStore', () => {
  const allNotifications = ref<Notification[] | undefined>();
  const unreadOnly = ref(true);
  const pusherStore = usePusherStore();
  const { userChannel } = storeToRefs(pusherStore);
  const { allUserIds } = storeToRefs(useUserStore());
  const { users } = storeToRefs(useRtmStore());
  const router = useRouter();
  const notificationsCount = ref(0);
  const pageTotal = ref(0);
  const unreadNotificationsCount = ref(0);
  const currentPage = ref(1);
  const loadedIds: ref<Set<string>> = ref(new Set());
  const pageSize = ref(10);

  const notificationToNotificatonWithUserData = (
    notification: Notification
  ) => {
    if (notification.user && !notification.userData) {
      const userData = users.value.find((u) => u._id === notification.user);
      if (userData) {
        return {
          ...notification,
          userData,
        };
      }
    }
    return notification;
  };

  const refresh = async (fromPage?: number) => {
    const data = await getNotifications(
      unreadOnly.value,
      fromPage !== undefined ? fromPage : currentPage.value,
      pageSize.value,
      allUserIds.value
    );
    if (data) {
      allNotifications.value = [];
      upsertNotifications(
        data.data.data.map(notificationToNotificatonWithUserData)
      );
      notificationsCount.value = data.data.totalCount;
      pageTotal.value = data.data.pageTotal;
      unreadNotificationsCount.value = data.data.totalUnreadCount;
      loadedIds.value = new Set(allNotifications.value.map((n) => n._id));
    }
    return data;
  };

  const startup = (withNotifications?: PagedNotifications) => {
    if (withNotifications) {
      allNotifications.value = [];
      upsertNotifications(
        withNotifications.data.map(notificationToNotificatonWithUserData)
      );
      notificationsCount.value = withNotifications.totalCount;
      pageTotal.value = withNotifications.pageTotal;
      unreadNotificationsCount.value = withNotifications.totalUnreadCount;
      if (allNotifications.value) {
        loadedIds.value = new Set(allNotifications.value.map((n) => n._id));
      }
    } else {
      refresh();
    }
  };

  const clear = () => {
    allNotifications.value = undefined;
    notificationsCount.value = 0;
    pageTotal.value = 0;
    unreadNotificationsCount.value = 0;
    loadedIds.value = new Set();
  };

  const upsertNotifications = (
    rawNotifications: Notification[],
    refreshIfNeeded = false
  ): number => {
    const newNotifications = rawNotifications.map((n) => {
      return {
        ...n,
        message: n.message?.replace(/\{datetime:(.*?)\}/g, (match, datetime) =>
          dateTimeToFormattedString(datetime)
        ),
      };
    });
    if (allNotifications.value === undefined) {
      allNotifications.value = newNotifications;
      return newNotifications.length;
    }
    let i = newNotifications.length - 1;
    let anyUnreadChange = false;
    while (i >= 0) {
      const existingIndex = allNotifications.value.findIndex(
        (c) => c._id === newNotifications[i]._id
      );

      if (existingIndex >= 0) {
        const wasRead = allNotifications.value[existingIndex].readDate
          ? true
          : false;
        if (!wasRead && newNotifications[i].readDate) {
          unreadNotificationsCount.value = unreadNotificationsCount.value - 1;
          anyUnreadChange = true;
        } else if (wasRead && !newNotifications[i].readDate) {
          unreadNotificationsCount.value = unreadNotificationsCount.value + 1;
          anyUnreadChange = true;
        }
        allNotifications.value.splice(
          existingIndex,
          1,
          notificationToNotificatonWithUserData(newNotifications[i])
        );
        newNotifications.splice(i, 1);
      }
      i = i - 1;
    }
    // append new notifications and sort by createdAt
    allNotifications.value = [
      ...allNotifications.value,
      ...newNotifications.map(notificationToNotificatonWithUserData),
    ].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    if (newNotifications.some((n) => !n.readDate)) {
      anyUnreadChange = true;
    }
    if (refreshIfNeeded && unreadOnly.value && anyUnreadChange) {
      // this will fix the current page and the unread count if necessary
      refresh();
    }
    return newNotifications.length;
  };

  // const deleteNotification = (notificationId: string) => {
  //   if (allNotifications.value) {
  //     allNotifications.value = allNotifications.value.filter(
  //       (c) => c._id !== notificationId
  //     );
  //   }
  // };

  const getNotification = async (
    notificationId: string,
    forceRefresh = false
  ) => {
    let notification = allNotifications.value?.find(
      (n) => n._id === notificationId
    );
    if (!notification || forceRefresh) {
      const { data } = await getOneNotification(notificationId);
      notification = data;
      upsertNotifications([notification], true);
    }
    return notification;
  };

  const toggleNotificationsAsRead = async (
    notificationIds: string[],
    read: boolean
  ) => {
    const { data: updatedNotifications } = await markNotificationsAsRead(
      notificationIds,
      read
    );
    if (
      allNotifications.value &&
      updatedNotifications &&
      updatedNotifications.length > 0
    ) {
      upsertNotifications(updatedNotifications, true);
    } else {
      if (read) {
        unreadNotificationsCount.value =
          unreadNotificationsCount.value - notificationIds.length;
      } else {
        unreadNotificationsCount.value =
          unreadNotificationsCount.value + notificationIds.length;
      }
    }
  };

  watch(
    [unreadOnly, allUserIds],
    ([newUnread, newUserIds], [oldUnread, oldUserIds]) => {
      if (newUnread !== oldUnread && currentPage.value !== 1) {
        // this will cause a refresh
        currentPage.value = 1;
      } else {
        if (newUserIds.length > 0) {
          refresh();
        }
      }
    }
  );

  const setCurrentPage = (page: number) => {
    currentPage.value = page;

    return refresh();
  };

  const unreadNotifications = computed((): Notification[] | undefined => {
    if (allNotifications.value === undefined) {
      return undefined;
    }
    return allNotifications.value.filter((n) => !n.readDate);
  });

  // const unreadNotificationsCount = computed(
  //   () => unreadNotifications.value?.length || 0
  // );

  const notifications = computed(() => {
    if (unreadOnly.value) {
      return unreadVisibleNotifications.value;
    }
    return allVisibleNotifications.value;
  });

  const pushNotificationToTop = (notification: Notification) => {
    if (allVisibleNotifications.value?.length === pageSize.value) {
      const pop = allVisibleNotifications.value[pageSize.value - 1];
      loadedIds.value.delete(pop._id);
    }
    if (!allNotifications.value) {
      return;
    }
    const existingIndex = allNotifications.value.findIndex(
      (c) => c._id === notification._id
    );
    if (existingIndex >= 0) {
      allNotifications.value.splice(existingIndex, 1);
    }
    allNotifications.value.unshift(notification);
    loadedIds.value.add(notification._id);
  };

  watch(
    userChannel,
    (v) => {
      if (v) {
        v.bind('notification', async (data: { notificationId: string }) => {
          const notification = await getNotification(data.notificationId, true);
          if (!notification.readDate) {
            unreadNotificationsCount.value = unreadNotificationsCount.value + 1;
            if (currentPage.value === 1) {
              pushNotificationToTop(notification);
            }
          }
          if (notification && !notification.readDate) {
            toastInfo(notification.title, undefined, {
              label: 'Details',
              onClick: () => {
                router.push({ path: `/inbox/${notification._id}` });
              },
            });
          }
        });
      }
    },
    { immediate: true }
  );

  const allVisibleNotifications = computed(() => {
    if (!allNotifications.value) {
      return undefined;
    } else {
      return allNotifications.value.filter((n) => loadedIds.value.has(n._id));
    }
  });

  const unreadVisibleNotifications = computed(() => {
    if (!unreadNotifications.value) {
      return undefined;
    } else {
      return unreadNotifications.value.filter((n) =>
        loadedIds.value.has(n._id)
      );
    }
  });

  const getNextOrPreviousNotificationPagingIfNecessary = async (
    notificationId: string,
    direction: number,
    bestGuessIndex: number
  ) => {
    if (!notifications.value) {
      return undefined;
    }
    let currentIndex = notifications.value.findIndex(
      (n) => n._id === notificationId
    );
    if (currentIndex === -1) {
      currentIndex = bestGuessIndex;
      if (
        direction > 0 &&
        currentIndex >= 0 &&
        currentIndex < notifications.value.length
      ) {
        return notifications.value[currentIndex];
      }
    }
    if (currentIndex === -1) {
      currentPage.value = 1;

      await refresh();
      let i = 0;
      if (allNotifications.value && allNotifications.value.length > 0) {
        // if we don't have an index, it means we were looking at a notification not visible
        // on the current page; so we just start over at the first page. In the case of a new
        // notification, this means it could probably be the first item on the new page we just
        // loaded, so skip over it
        if (allNotifications.value[i]._id === notificationId) {
          i = i + 1;
        }
      }
      return allNotifications.value?.[i];
    }
    if (direction < 0) {
      if (currentIndex > 0 && notifications.value) {
        return notifications.value[currentIndex - 1];
      } else if (currentPage.value > 1) {
        currentPage.value = currentPage.value - 1;

        await refresh();
        return allNotifications.value?.[allNotifications.value.length - 1];
      }
    } else if (direction > 0) {
      if (
        notifications.value &&
        currentIndex < notifications.value.length - 1
      ) {
        return notifications.value[currentIndex + 1];
      } else if (currentPage.value < pageTotal.value) {
        currentPage.value = currentPage.value + 1;

        await refresh();
        return allNotifications.value?.[0];
      }
    }
    return undefined;
  };
  return {
    // these are all the notifications the user can see on the current page; will be either
    // allVisibleNotifications or unreadVisibleNotifications
    notifications,
    // count of all the unread notifications; may be updated locally
    unreadNotificationsCount,
    // count of all the notifications according to the BE; only counts unread if we are in unread
    // mode
    notificationsCount,
    // whether or not we are only looking at unread
    unreadOnly,
    // total number of pages of notifications
    pageTotal,
    // current page we are looking at
    currentPage,
    // number of notifications per page
    pageSize,

    // actions
    // called after login
    startup,
    // refresh the current page
    refresh,
    // clear all loaded notifications
    clear,
    // update/add notifications; new notifications will not be made visible on current page
    // unless it is reloaded
    upsertNotifications,
    // deleteNotification,
    // locally marks notification as read and updates the server
    toggleNotificationsAsRead,
    // fetch single notification
    getNotification,
    // get the next or previous notification out of all the notifications (considering if we are
    // in unreadOnly mode). Page forward or backward if needed
    getNextOrPreviousNotificationPagingIfNecessary,
    // set the current page and load
    setCurrentPage,
  };
});
